import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { DEVICES_INITIAL_STATE, DeviceState } from './devices.model';
import { withCallState } from '@shared/store-features/call-state-feature';
import { CallStateEnum } from '@shared/models/call-state.model';
import { SnackbarType } from '@shared/models/app-snackbar.model';
import useSnackbarStore from '@store/snackbar-store/snackbar.store';
import {
  activateDeviceWithCode,
  assignScreenDigitalSignageDevices,
  deleteDigitalSignageDeviceOrDevices,
  reloadDevices
} from '@shared/api/digital-signage/devices-api';
import axios from 'axios';
import { getDeviceScreensList } from '@api/digital-signage/screens-api.ts';
import {
  getDeviceGroupsList,
  moveDevicesToGroup as moveDevicesToGroupApi
} from '@api/digital-signage/device-groups-api.ts';

const { showSnackbar } = useSnackbarStore.getState();

const useDeviceStore = create<DeviceState>()(
  devtools(
    (set, getState) => ({
      ...DEVICES_INITIAL_STATE,
      ...withCallState(getState),

      getScreens: async () => {
        try {
          const screensData = await getDeviceScreensList();

          const data = screensData.map((item) => ({
            id: item._id,
            value: item.title,
            title: item.title
          }));

          set({
            screensData: data
          });
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            useSnackbarStore
              .getState()
              .showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      getDeviceGroupsOptions: async () => {
        set({ optionsCallState: CallStateEnum.Loading });
        try {
          const deviceGroups = await getDeviceGroupsList();

          set({
            deviceGroupsData: deviceGroups.map((item) => ({
              id: item._id,
              title: item.name,
              value: item.name
            })),
            optionsCallState: CallStateEnum.Loaded
          });
        } catch (error) {
          set({ optionsCallState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            useSnackbarStore
              .getState()
              .showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      reloadDevices: async (body, onSuccess?: () => void) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await reloadDevices(body);
          set({ callState: CallStateEnum.Loaded });
          showSnackbar(
            'snackbar_success_device_reload_text',
            SnackbarType.Success
          );
          onSuccess?.();
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (error instanceof Error) {
            showSnackbar(
              'snackbar_error_digital_signage_text',
              SnackbarType.Error
            );
          }
        }
      },

      deleteRow: async ({ data, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await deleteDigitalSignageDeviceOrDevices(data);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_delete_digital_signage_device_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (error instanceof Error) {
            showSnackbar(
              'snackbar_error_digital_signage_text',
              SnackbarType.Error
            );
          }
        }
      },

      activateDeviceWithCode: async ({ data, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await activateDeviceWithCode(data);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_success_device_activate_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const statusCode = error?.response?.data?.statusCode;

            if (statusCode === 600) {
              showSnackbar(
                'snackbar_error_invalid_code_text',
                SnackbarType.Error
              );
            } else {
              const message = error.response?.data?.message;
              useSnackbarStore
                .getState()
                .showSnackbar(message, SnackbarType.Error);
            }
          }
        }
      },

      assignScreenDigitalSignageDevice: async ({
        data,
        state,
        handleClose
      }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await assignScreenDigitalSignageDevices(data);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_success_device_assign_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (error instanceof Error) {
            showSnackbar(
              'snackbar_error_digital_signage_text',
              SnackbarType.Error
            );
          }
        }
      },

      moveDevicesToGroup: async ({ body, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await moveDevicesToGroupApi(body);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_success_move_devices_to_group_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (axios.isAxiosError(error)) {
            const message = error.response?.data?.message;
            useSnackbarStore
              .getState()
              .showSnackbar(message, SnackbarType.Error);
          }
        }
      },

      isOptionsLoading: () =>
        getState().optionsCallState === CallStateEnum.Loading
    }),

    { name: 'DigitalSignageStore' }
  )
);

export default useDeviceStore;
